/* Browser copy of supabase/functions/_shared/phone-canonical.ts — keep both in sync. */
/**
 * Canonical E.164 normalisation — single source of truth for every country.
 *
 * Strategy (validate-then-repair, never store an unvalidated string):
 *   1. Build a small set of *candidate* interpretations of the raw input
 *      (as typed, IDD-stripped, trunk-zero-stripped, duplicated-country-code
 *      collapsed, default-country prefixed, …).
 *   2. Hand every candidate to libphonenumber's global metadata and keep only
 *      the ones it reports as a *valid* number for a real numbering plan.
 *   3. Return the first valid candidate in priority order, or `null`.
 *
 * Because step 2 is metadata-driven, this works for all ~240 numbering plans
 * without any hand-maintained per-country rules: AU `04xx` trunk zero, US
 * duplicated `+1 1…`, UK `07…`, `00`/`011` IDD prefixes, etc. all collapse to
 * the same canonical string.
 *
 * A `null` result MUST be treated as a hard validation failure by callers —
 * that is what prevents malformed numbers (e.g. `+113466072021`) from ever
 * reaching `profiles.phone_number` and creating duplicate accounts.
 */

import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';

export interface CanonicalPhoneResult {
  /** Canonical E.164, e.g. "+61412345678" */
  e164: string;
  /** ISO-3166 alpha-2 of the numbering plan, e.g. "AU" */
  country?: string;
  /** Which repair rule produced the accepted candidate (for logging/telemetry) */
  rule: string;
}

interface Candidate {
  value: string;
  rule: string;
}

/** All distinct calling codes, longest first so "+1 1…" style collapses are unambiguous. */
let CALLING_CODES: string[] | null = null;
function callingCodes(): string[] {
  if (CALLING_CODES) return CALLING_CODES;
  const set = new Set<string>();
  for (const iso of getCountries()) {
    try {
      set.add(getCountryCallingCode(iso as CountryCode));
    } catch { /* ignore */ }
  }
  CALLING_CODES = [...set].sort((a, b) => b.length - a.length);
  return CALLING_CODES;
}

function isoForCallingCode(code: string): CountryCode | undefined {
  // +61 is deliberately pinned to AU (primary market) to avoid territories
  // that share the calling code winning the metadata lookup.
  if (code === '61') return 'AU';
  if (code === '1') return 'US';
  return getCountries().find((iso) => {
    try {
      return getCountryCallingCode(iso as CountryCode) === code;
    } catch {
      return false;
    }
  }) as CountryCode | undefined;
}

function validate(raw: string, rule: string, defaultCountry?: CountryCode): CanonicalPhoneResult | null {
  try {
    const parsed = raw.startsWith('+')
      ? parsePhoneNumberFromString(raw)
      : parsePhoneNumberFromString(raw, defaultCountry);
    if (parsed?.isValid()) {
      return { e164: parsed.format('E.164'), country: parsed.country, rule };
    }
  } catch { /* not a candidate */ }
  return null;
}

/**
 * Canonicalise any user-entered phone number to E.164.
 *
 * @param input          raw user input (spaces/dashes/parens/+ all tolerated)
 * @param defaultCountry optional ISO-2 (e.g. "AU") or calling code (e.g. "61")
 *                       used when the input carries no country context
 * @returns canonical result, or null when no valid interpretation exists
 */
export function canonicalizePhone(
  input: string,
  defaultCountry?: string,
): CanonicalPhoneResult | null {
  if (!input) return null;

  const raw = String(input).trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 5 || digits.length > 20) return null;

  // Resolve the caller-supplied default country (ISO-2 or calling code).
  let iso: CountryCode | undefined;
  const hint = String(defaultCountry ?? '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(hint) && getCountries().includes(hint as CountryCode)) {
    iso = hint as CountryCode;
  } else if (/^\d{1,4}$/.test(hint.replace(/\D/g, '')) && hint.replace(/\D/g, '')) {
    iso = isoForCallingCode(hint.replace(/\D/g, ''));
  }

  const candidates: Candidate[] = [];
  const push = (value: string, rule: string) => {
    if (value && !candidates.some((c) => c.value === value && c.rule === rule)) {
      candidates.push({ value, rule });
    }
  };

  // 1. Exactly as typed (handles already-correct international input).
  push(raw, 'as_typed');

  // 2. IDD prefixes: 00… / 011… → +…
  if (digits.startsWith('00') && digits.length > 7) push('+' + digits.slice(2), 'idd_00');
  if (digits.startsWith('011') && digits.length > 8) push('+' + digits.slice(3), 'idd_011');

  // 3. Plain digit string treated as international.
  push('+' + digits, 'digits_as_e164');

  for (const cc of callingCodes()) {
    if (!digits.startsWith(cc)) continue;
    const national = digits.slice(cc.length);
    if (national.length < 4) continue;

    // 4. Trunk zero left in after the country code: +61 0412… → +61 412…
    if (national.startsWith('0')) push('+' + cc + national.replace(/^0+/, ''), 'trunk_zero_after_cc');

    // 5. Duplicated country code: +1 1346… / +61 61 4… → collapse one copy.
    //    This is the exact defect that created the +113466072021 duplicate.
    if (national.startsWith(cc)) push('+' + cc + national.slice(cc.length), 'duplicated_cc');

    // 6. Duplicated country code *and* trunk zero: +61 0 61 4…
    if (national.startsWith('0' + cc)) push('+' + cc + national.slice(1 + cc.length), 'duplicated_cc_trunk_zero');
  }

  // 7. National format against the selected/default country (handles AU 04xx,
  //    UK 07xxx, NZ 021…, and the "typed without the leading 0" shorthand).
  if (iso) {
    push(raw, 'national_default_country');
    push(digits, 'national_digits_default_country');
    if (digits.startsWith('0')) push(digits.replace(/^0+/, ''), 'national_no_trunk_zero');
    else push('0' + digits, 'national_add_trunk_zero');
    try {
      const cc = getCountryCallingCode(iso);
      push('+' + cc + digits.replace(/^0+/, ''), 'default_cc_prefix');
    } catch { /* ignore */ }
  }

  for (const candidate of candidates) {
    const result = validate(candidate.value, candidate.rule, iso);
    if (result) return result;
  }

  return null;
}

/**
 * Strict variant for write paths: returns the canonical E.164 string or throws.
 * Use this anywhere a number is about to be persisted or used to create an
 * identity (signup, OTP verify, provisioning).
 */
export function requireCanonicalPhone(input: string, defaultCountry?: string): string {
  const result = canonicalizePhone(input, defaultCountry);
  if (!result) {
    throw new Error('INVALID_PHONE');
  }
  return result.e164;
}

/**
 * Legacy-tolerant helper for *read* paths: returns the canonical form when the
 * number is valid, otherwise a best-effort E.164-shaped string so historical
 * rows written before canonicalisation can still be looked up.
 */
export function canonicalOrRaw(input: string, defaultCountry?: string): string {
  const result = canonicalizePhone(input, defaultCountry);
  if (result) return result.e164;
  const digits = String(input ?? '').replace(/\D/g, '');
  return digits ? '+' + digits : '';
}
